import React from "react";

import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { Checkbox, IconButton, Stack, styled, TableCell, TableRow, Typography } from "@mui/material";
import { ReportGeneratorIcon } from "assets/material-custom-icons";
import { AddValueButton, EditableCell } from "components/File-repository/Table/Table";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { DealBase } from "store/types";

import { FileType } from "../../../constants";
import { FileValues } from "./FilesUpload";
import { useFilesTableRowHook } from "./useFilesTableRowHook";

type InvestorOption = { id: number; name: string };

export const BORDER_DIVIDER = "1px solid #dbe8f5";
export const CELL_PADDING = { py: 1, px: 1.25 };

export const StyledTableCellBase = styled(TableCell)({
  borderBottom: BORDER_DIVIDER,
  ...CELL_PADDING
});

export const StyledTableCellCheckbox = styled(TableCell)<{ $header?: boolean }>(({ $header }) => ({
  width: 32,
  minWidth: 32,
  borderBottom: BORDER_DIVIDER,
  py: $header ? 0 : 0.625
}));

export const StyledTableCellBody = styled(StyledTableCellBase)({
  fontSize: 14,
  color: "#071f35"
});

export const StyledTableRow = styled(TableRow)({
  "& td": { borderBottom: BORDER_DIVIDER },
  "&:last-child td": { borderBottom: 0 }
});

export const StyledFileName = styled(Typography)({
  fontSize: 14,
  color: "#071f35",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  display: "block",
  maxWidth: "100%"
});

// Map a local File to the FileType FileViewer understands; null means it can't be previewed in-app.
export const getPreviewType = (file: File): FileType | null => {
  if (file.type === "application/pdf") return FileType.PDF;
  if (file.type === "image/png") return FileType.PNG;
  if (file.type === "image/jpeg" || file.type === "image/jpg") return FileType.JPEG;
  return null;
};

interface Props {
  fileValue: FileValues;
  index: number;
  isSelected: boolean;
  isCloudMirror?: boolean;
  lockedDealId?: number | null;
  disableRemove: boolean;
  setFilesValues: React.Dispatch<React.SetStateAction<FileValues[]>>;
  updateDocumentType: (index: number, value: string | null) => void;
  updateEffectiveDate?: (index: number, value: string | null) => void;
  onDealChange?: (index: number, deals: DealBase[]) => void;
  dealInvestors: InvestorOption[];
  dealInvestorsLoading: boolean;
  dealInvestorsMap?: Record<number, InvestorOption[]>;
  onSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onPreview: (file: File) => void;
  onRemove: (index: number) => void;
}

export const FilesTableRow: React.FC<Props> = ({
  fileValue,
  index,
  isSelected,
  isCloudMirror,
  lockedDealId,
  disableRemove,
  setFilesValues,
  updateDocumentType,
  updateEffectiveDate,
  onDealChange,
  dealInvestors,
  dealInvestorsLoading,
  dealInvestorsMap,
  onSelect,
  onPreview,
  onRemove
}) => {
  const {
    file,
    document_type,
    investor_id,
    document_type_ai_suggested,
    dealLabel,
    dealTooltip,
    investorName,
    effectiveDateLabel,
    investorDisabled,
    openDealPrompt,
    openClassificationPrompt,
    openInvestorPrompt,
    openEffectiveDatePrompt
  } = useFilesTableRowHook({
    fileValue,
    index,
    setFilesValues,
    updateDocumentType,
    updateEffectiveDate,
    onDealChange,
    dealInvestors,
    dealInvestorsLoading,
    dealInvestorsMap,
    isCloudMirror
  });

  return (
    <StyledTableRow>
      <StyledTableCellCheckbox padding="checkbox">
        <Checkbox checked={isSelected} onChange={onSelect} sx={{ p: 0.5 }} />
      </StyledTableCellCheckbox>
      <StyledTableCellBody>
        <MaterialTooltip
          isShow
          variant="dark"
          placement="top"
          title={getPreviewType(file) ? "Click to preview" : file.name}
        >
          <StyledFileName
            onClick={getPreviewType(file) ? () => onPreview(file) : undefined}
            sx={
              getPreviewType(file)
                ? { color: "#26A6AE", cursor: "pointer", "&:hover": { textDecoration: "underline" } }
                : undefined
            }
          >
            {file.name}
          </StyledFileName>
        </MaterialTooltip>
      </StyledTableCellBody>
      {isCloudMirror && (
        <StyledTableCellBase>
          <Stack direction="row" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
            {dealLabel ? (
              <EditableCell
                disabled={!!lockedDealId}
                value={dealLabel}
                tooltip={dealTooltip}
                onClick={openDealPrompt}
              />
            ) : (
              <AddValueButton disabled={!!lockedDealId} onClick={openDealPrompt} />
            )}
          </Stack>
        </StyledTableCellBase>
      )}
      <StyledTableCellBase>
        <Stack direction="row" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
          {document_type_ai_suggested && <ReportGeneratorIcon sx={{ fontSize: 20, color: "#c09560", flexShrink: 0 }} />}
          {document_type ? (
            <EditableCell value={document_type} onClick={openClassificationPrompt} />
          ) : (
            <AddValueButton onClick={openClassificationPrompt} />
          )}
        </Stack>
      </StyledTableCellBase>
      <StyledTableCellBase
        sx={{
          ...(!isCloudMirror && {
            paddingRight: "0px !important"
          })
        }}
      >
        <Stack direction="row" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
          {investor_id ? (
            <EditableCell disabled={investorDisabled} value={investorName} onClick={openInvestorPrompt} />
          ) : (
            <AddValueButton disabled={investorDisabled} onClick={openInvestorPrompt} />
          )}
        </Stack>
      </StyledTableCellBase>

      <StyledTableCellBase sx={{ paddingRight: "0px !important" }}>
        {fileValue.effective_date ? (
          <EditableCell value={effectiveDateLabel} onClick={openEffectiveDatePrompt} />
        ) : (
          <AddValueButton onClick={openEffectiveDatePrompt} />
        )}
      </StyledTableCellBase>
      <StyledTableCellBody align="center" sx={{ width: 20, px: 0.5, whiteSpace: "nowrap" }}>
        <MaterialTooltip isShow variant="dark" placement="top" title="Delete">
          <IconButton size="sm" onClick={() => onRemove(index)} disabled={disableRemove} sx={{ color: "#071F35" }}>
            <DeleteOutlineOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </MaterialTooltip>
      </StyledTableCellBody>
    </StyledTableRow>
  );
};
